import * as d3 from "d3";
import {colonAnnotation, createTooltip} from "@/common/functions";

export default function (chart, divId, vizName, resData) {
    d3.select(divId).selectAll("*").remove();

    /* ---------------- CONFIG ---------------- */
    const rowHeight = 26;
    const groupGap = 8;

    const parseTime = d3.timeParse("%Y-%m-%d %H:%M:%S");

    /* ---------------- DATA ---------------- */
    const raw = resData.map(d => ({
        ...d,
        group: d.dim1_name,
        task: d.dim3_name,
        start0: parseTime(d.dim8_name),
        end0: parseTime(d.dim9_name)
    })).filter(d => d.start0 && d.end0);

    /* ---- Deduplicate (group + task) ---- */
    const map = {};
    raw.forEach(d => {
        const k = d.group + "||" + d.task;
        if (!map[k]) map[k] = { ...d };
        else {
            if (d.start0 < map[k].start0) map[k].start0 = d.start0;
            if (d.end0 > map[k].end0) map[k].end0 = d.end0;
        }
    });

    const deduped = Object.values(map);

    /* ---- Group ---- */
    const nested = d3.nest()
        .key(d => d.group)
        .entries(deduped);

    /* ---- Force sequential execution ---- */
    const data = [];
    nested.forEach(g => {
        g.values.sort((a, b) => d3.ascending(a.start0, b.start0));
        let t = g.values[0].start0;

        g.values.forEach(v => {
            const dur = v.end0 - v.start0;
            data.push({
                ...v,
                group: g.key,
                task: v.task,
                start: new Date(t),
                end: new Date(t.getTime() + dur)
            });
            t = new Date(t.getTime() + dur);
        });
    });

    /* ---------------- SCALES ---------------- */
// Calculate actual time range
//     const minStart = d3.min(data, d => d.start);
    // const maxEnd = d3.max(data, d => d.end);


// Create scale with padding
//     const padding = 5000; // 5 seconds padding
    // const xDomain = [
    //     new Date(minStart.getTime() - padding),
    //     new Date(maxEnd.getTime() + padding)
    // ];

    const x = d3.scaleTime()
        // .domain(xDomain)
        .range([0, chart.width + chart.margin.left+chart.margin.bottom])
        // .nice(); // This helps with tick placement


    const color = d3.scaleOrdinal(d3.schemeCategory10)
        .domain(nested.map(d => d.key));

    /* ---------------- STATE ---------------- */
    const collapsed = {};
    nested.forEach(d => collapsed[d.key] = false);

    /* ---------------- CONTAINERS ---------------- */
    // --- 2. Clear old SVG and Content ---
    const container = d3.select(divId);
    container.selectAll('*').remove(); // Clear everything, not just 'svg'

    // Ensure container is positioned for sticky context
    container
        .style('position', 'relative')
        .style('overflow', 'auto') // Ensure the container scrolls
        .style('height', '100%');  // Or specific height if needed

    const axisDiv = container
        .append("div")
        .style("position", "sticky")
        .style("top", "0px")
        .style("background", "#fff")
        .style("z-index", 10);

    const axisSvg = axisDiv
        .append('svg')
        .attr('width', chart.width + 2 * chart.margin.left + chart.margin.right)
        .attr('height',50)

    const svg = container
        .append('svg')
        .attr('width', chart.width + 2 * chart.margin.left + chart.margin.right)
        .attr('height', chart.height + 5 * chart.margin.top + chart.margin.bottom)

    const g = svg.append("g")
        .attr("transform", `translate(${chart.margin.left-120},${chart.margin.top})`);

    // 🔥 BACKGROUND RECT (ONLY ONCE)
    const plotBg = g.append("rect")
        .attr("class", "plot-bg")
        .attr("x", 0)
        .attr("y", 0)
        .attr("fill", "#d8dddd");

// 🔥 LAYERS
    const plotLayer = g.append("g").attr("class", "plot-layer");
    const labelLayer = g.append("g").attr("class", "label-layer");


    const tooltip = createTooltip(divId);

    /* ---------------- X AXIS (DRAW ONCE) ---------------- */
    const xAxisG = axisSvg.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(${chart.margin.left - 120},${chart.margin.top})`);


    /* ---------------- RENDER ---------------- */
    function render() {
        plotLayer.selectAll("*").remove();
        labelLayer.selectAll("*").remove();

        const rows = [];
        let r = 0;

        nested.forEach(grp => {
            rows.push({ row: r++, type: "group", label: grp.key });

            if (!collapsed[grp.key]) {
                data.filter(d => d.group === grp.key)
                    .forEach(v => rows.push({ row: r++, type: "task", ...v }));
            }

            r += groupGap / rowHeight;
        });


        // 🔥 visible tasks only
        const visibleTasks = rows.filter(d => d.type === "task");

        if (visibleTasks.length) {
            const minStart = d3.min(visibleTasks, d => d.start);
            const maxEnd = d3.max(visibleTasks, d => d.end);

            const padding = 2000; // 2 sec padding
            x.domain([
                new Date(minStart.getTime() - padding),
                new Date(maxEnd.getTime() + padding)
            ]).nice();

            // 🔥 redraw axis
            xAxisG.call(
                d3.axisTop(x)
                    .ticks(10)
                    .tickFormat(d3.timeFormat("%y-%m-%d %H:%M:%S"))
            );

            xAxisG.selectAll("text").style("font-size", "11px");
        }



        const height = r * rowHeight;
        svg.attr("height", height + chart.margin.top + chart.margin.bottom);

        const y = d3.scaleBand()
            .domain(rows.map(d => d.row))
            .range([0, height])
            .paddingInner(0.15);

        plotBg
            .attr("width", chart.width + chart.margin.left+chart.margin.bottom)
            .attr("height", height+ chart.margin.top + chart.margin.bottom);


        /* ---- Group labels ---- */
        labelLayer.selectAll(".group")
            .data(rows.filter(d => d.type === "group"))
            .enter()
            .append("text")
            .attr("x", -14)
            .attr("y", d => y(d.row) + y.bandwidth() / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", "end")
            .style("font-weight", "600")
            .style("cursor", "pointer")
            .text(d => (collapsed[d.label] ? "▶ " : "▼ ") + d.label)
            .on("click", function(d) {
                collapsed[d.label] = !collapsed[d.label];
                render();
            });

        /* ---- Bars ---- */
        plotLayer.selectAll(".bar")
            .data(rows.filter(d => d.type === "task"))
            .enter()
            .append("rect")
            .attr("x", d => x(d.start))
            .attr("y", d => y(d.row))
            .attr("height", y.bandwidth())
            .attr("width", d => Math.max(8, x(d.end) - x(d.start)))
            .attr("rx", 4)
            .attr("fill", d => color(d.group))
            .on("mouseover", function(d) {
                // console.log(d3.event, d); // For debugging, log the event and data
                tooltip.onMouseOver(d, this, function(d) {
                    // Tooltip content callback
                    return chart.x1MetricTooltip + colonAnnotation + d.dim11_name + "<br/>" +
                        chart.x2MetricTooltip + colonAnnotation + d.metric2 + "<br/>" +
                        chart.y1MetricTooltip + colonAnnotation + d.dim10_name
                });
            })
            .on('mousemove', function(event) {
                tooltip.onMouseMove(event);  // Update tooltip position on mouse move
            })
            .on("mouseout", function() {
                // Hide the tooltip on mouse out
                tooltip.onMouseLeave();
            });

        /* ---- Text ---- */
        plotLayer.selectAll(".task-text")
            .data(rows.filter(d => d.type === "task"))
            .enter()
            .append("text")
            .attr("x", d => x(d.start) + 6)
            .attr("y", d => y(d.row) + y.bandwidth() / 2)
            .attr("dy", "0.35em")
            .text(d => d.task)
            .attr("fill", "#fff")
            .style("font-size", "11px");
    }

    render();
}
